import { useEffect, useState, useCallback } from "react";
import { fetchClients, createClient, updateClient, deleteClient } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { nome: "", telefone: "", email: "", cpf: "", endereco: "" };

export default function Clients({ embedded = false, onClientCreated }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");

  const reload = useCallback(() => fetchClients().then(setList), []);
  useEffect(() => { reload(); }, [reload]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefone) { toast.error("Nome e telefone são obrigatórios"); return; }
    try {
      if (editId) {
        await updateClient(editId, form);
        toast.success("Cliente atualizado");
      } else {
        const created = await createClient(form);
        if (onClientCreated && created?.id) {
          toast.success("Cliente criado", {
            action: {
              label: "+ Adicionar veículo",
              onClick: () => onClientCreated(created.id),
            },
            duration: 8000,
          });
        } else {
          toast.success("Cliente criado");
        }
      }
      setOpen(false); setForm(empty); setEditId(null);
      reload();
    } catch { toast.error("Erro ao salvar"); }
  };

  const onEdit = (c) => {
    setEditId(c.id);
    setForm({ nome: c.nome, telefone: c.telefone, email: c.email, cpf: c.cpf, endereco: c.endereco });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este cliente e todos os seus veículos?")) return;
    await deleteClient(id);
    toast.success("Cliente excluído");
    reload();
  };

  const filtered = list.filter((c) =>
    [c.nome, c.telefone, c.cpf, c.email].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()))
  );

  const newClientDialog = (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
      <DialogTrigger asChild>
        <Button data-testid="new-client-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">
          <Plus size={16} weight="bold" className="mr-1.5" /> Novo Cliente
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-heading">{editId ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Nome *</Label>
              <Input data-testid="client-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            </div>
            <div>
              <Label>Telefone *</Label>
              <Input data-testid="client-telefone" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input data-testid="client-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>CPF</Label>
              <Input data-testid="client-cpf" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Endereço</Label>
              <Textarea data-testid="client-endereco" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" data-testid="save-client-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const tableSection = (
    <div className="bg-white border border-[#E2E8F0] rounded-md">
      <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
          <Input
            data-testid="client-search"
            placeholder="Buscar por nome, telefone, CPF ou e-mail..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {embedded && newClientDialog}
      </div>
      {filtered.length === 0 ? (
        <EmptyState title="Nenhum cliente cadastrado" message="Clique em 'Novo Cliente' para começar." />
      ) : (
        <Table>
          <TableHeader className="bg-[#F8FAFC]">
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c, i) => (
              <TableRow key={c.id} className={i % 2 ? "bg-[#F8FAFC]" : ""} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-medium text-[#0F172A]">{c.nome}</TableCell>
                <TableCell>{c.telefone}</TableCell>
                <TableCell className="text-[#475569]">{c.email || "-"}</TableCell>
                <TableCell className="text-[#475569]">{c.cpf || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => onEdit(c)} data-testid={`edit-client-${c.id}`}>
                    <PencilSimple size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(c.id)} data-testid={`delete-client-${c.id}`}>
                    <Trash size={16} className="text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );

  if (embedded) return tableSection;

  return (
    <div data-testid="clients-page">
      <PageHeader
        title="Clientes"
        subtitle="Cadastro completo de clientes da oficina."
        actions={newClientDialog}
      />
      {tableSection}
    </div>
  );
}
